import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  withStyles,
  CssBaseline,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  TableCell,
  TableSortLabel,
  Input,
  IconButton,
  Icon,
  Table,
  TableHead,
  TableRow,
  CardHeader,
  TableBody,
} from '@material-ui/core';
import {
  DEFAULT_STYLES,
  DRAWER_NOTIFICATION_WIDTH,
  RXTABLEFIELD_ACTIVE_PAGE,
  TABLE_SORT_ASCENDING,
  TABLE_SORT_DESCENDING,
  RXTABLEFIELD_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  TABLE_FIELD_FIELD_ORDER,
  TABLE_FIELD_FIELD_STATUS,
  TABLE_FIELD_FIELD_TITLE,
  TABLE_FIELD_FIELD_NAME,
  RXFIELD_FIELD_ORDER,
} from '../../constant';
import { MainMenu, PageHeaderAdmin, LoadingIndicator } from '../../component';
import LocalizedString from '../../localization';
import { isSortFieldActive, getSortIcon } from '../../helper';
import FieldAddDialog from './field-add-dialog.container';
import FieldEditDialog from './field-edit-dialog.container';
import FieldDetailDialog from './field-detail-dialog.container';
import FieldDeleteDialog from './field-delete-dialog.container';
import FieldFilterDialog from './field-filter-dialog.container';

const styles = theme => ({
  backIcon: {
    ...DEFAULT_STYLES.WEB_USER.BACK_ICON,
  },
  btnNewBooking: {
    backgroundColor: '#5CB860',
    color: '#ffffff',
    width: '256px',
    '&:hover': {
      backgroundColor: '#558b2f',
    },
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
  },
  contentMenuDrawerOpen: {
    [theme.breakpoints.up('md')]: {
      marginLeft: '256px',
      width: 'auto',
    },
  },
  contentDrawerOpen: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${DRAWER_NOTIFICATION_WIDTH}px)`,
    },
  },
  contentDrawerClosed: {
    width: '100%',
  },
  detailHeader: {
    color: '#0066CB',
  },
  grow: {
    flexGrow: 1,
  },
  itemPerRowContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  pageNavigatorContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  pageTitle: {
    color: '#0266B4',
  },
  tablePagination: {
    display: 'flex',
    justifyContent: 'space-between',
    flexGrow: 1,
    marginTop: '24px',
  },
  tablePaginationPageInput: {
    flexGrow: 1,
    display: 'inline-block',
    textAlign: 'center',
    verticalAlign: 'baseline',
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.background.default,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
});

class FieldBase extends Component {
  componentDidMount() {
    this.props.onAppear();
    this.handleChangeSort('order');
  }

  handleChangeSort = (newSortField) => {
    const { sortField, sortDirection, onChangeSort } = this.props;

    if (newSortField === sortField) {
      onChangeSort(
        sortField,
        sortDirection === TABLE_SORT_ASCENDING
          ? TABLE_SORT_DESCENDING
          : TABLE_SORT_ASCENDING,
      );
    } else {
      onChangeSort(sortField, TABLE_SORT_ASCENDING);
    }
  };

  renderTableItemPerRow = () => {
    const { classes, pageSize, onChangeRow } = this.props;

    return (
      <div className={classes.itemPerRowContainer}>
        <Select
          value={pageSize}
          onChange={event => onChangeRow(
            RXTABLEFIELD_PAGE_SIZE,
            event.target.value,
          )}
        >
          {DEFAULT_PAGE_SIZE_OPTIONS.map(item => (
            <MenuItem value={item} key={item}>
              {item}
              &nbsp;
              {LocalizedString.common.labelRows}
            </MenuItem>
          ))}
        </Select>
      </div>
    );
  }

  renderTableCellHeader = (label, fieldId) => {
    const { sortField, sortDirection } = this.props;
    return (
      <TableCell>
        <TableSortLabel
          active={
            isSortFieldActive(sortField, fieldId)
          }
          IconComponent={getSortIcon(
            isSortFieldActive(sortField, fieldId),
            sortDirection,
          )}
          onClick={
            () => this.handleChangeSort(fieldId)
          }
        >
          {label}
        </TableSortLabel>
      </TableCell>
    );
  }

  renderTableCellStringFilter = (field, hidden = false) => {
    const { filters, onChangeFilter } = this.props;

    if (!hidden) {
      return (
        <TableCell>
          <Input
            type="text"
            fullWidth
            value={filters[field]}
            onChange={
            ({ nativeEvent }) => onChangeFilter(
              field,
              nativeEvent.target.value,
            )
          }
            placeholder={LocalizedString.common.placeholderSearch}
          />
        </TableCell>
      );
    }
    return (
      <TableCell />
    );
  }

  renderTableCellBody = (text, field = '') => (
    <TableCell>
      <Typography>
        {field === RXFIELD_FIELD_ORDER ? text : (text || '-')}
      </Typography>
    </TableCell>
  );

  renderTableCellAction = (user) => {
    const {
      classes, onEditPressed,
      onDetailPressed, onDeletePressed,
    } = this.props;
    return (
      <TableCell>
        <IconButton
          color="primary"
          className={classes.iconButtonPadding4}
          onClick={() => onEditPressed(user.id)}
        >
          <Icon>edit</Icon>
        </IconButton>
        <IconButton
          color="primary"
          className={classes.iconButtonPadding4}
          onClick={() => onDetailPressed(user.id)}
        >
          <Icon>info</Icon>
        </IconButton>
        <IconButton
          color="primary"
          className={classes.iconButtonPadding4}
          onClick={() => onDeletePressed(user.id)}
        >
          <Icon>delete</Icon>
        </IconButton>
      </TableCell>
    );
  }

  renderTablePagination = () => {
    const {
      classes, activePage, totalPage, onChangePaging,
    } = this.props;

    if (totalPage - 1 !== 0) {
      return (
        <div className={classes.tablePagination}>
          <Button
            variant="contained"
            style={{ flexGrow: 1 }}
            disabled={activePage === 0}
            onClick={() => onChangePaging(RXTABLEFIELD_ACTIVE_PAGE, activePage - 1)}
          >
            {LocalizedString.common.labelPagingPrevButton}
          </Button>

          <div className={classes.tablePaginationPageInput}>
            <span style={{ fontWeight: 500 }}>{LocalizedString.common.labelPagingPage}</span>
            <Input
              type="number"
              value={activePage}
              style={{ width: '50%' }}
              inputProps={{
                min: 1,
                max: totalPage,
                step: 1,
                style: { textAlign: 'center' },
              }}
              onChange={({ nativeEvent }) => onChangePaging(
                RXTABLEFIELD_ACTIVE_PAGE,
                parseInt(nativeEvent.target.value, 10),
              )}
            />
            <span style={{ fontWeight: 500 }}>
              {`${LocalizedString.common.labelPagingPageOf} ${totalPage}`}
            </span>
          </div>

          <Button
            variant="contained"
            style={{ flexGrow: 1 }}
            disabled={activePage === totalPage - 1}
            onClick={() => onChangePaging(RXTABLEFIELD_ACTIVE_PAGE, activePage + 1)}
          >
            {LocalizedString.common.labelPagingNextButton}
          </Button>
        </div>
      );
    }
    return (<></>);
  }

  render() {
    const {
      classes,
      menuOpen,
      downloading,
      fields,
      dialogAddStatus,
      dialogEditStatus,
      dialogDetailStatus,
      dialogDeleteStatus,
      dialogFilterStatus,
      onAddNewPressed,
      onFilterPressed,
      onAddDialogClosePressed,
      onEditDialogClosePressed,
      onDetailDialogClosePressed,
      onDeleteDialogClosePressed,
      onFilterDialogClosePressed,
    } = this.props;

    return (
      <>
        <div className={classes.root}>
          <CssBaseline />

          <PageHeaderAdmin />

          <MainMenu />

          <div
            className={
              classNames(classes.content, {
                [classes.contentMenuDrawerOpen]: menuOpen,
              })
            }
            style={{ padding: '16px' }}
          >
            <Grid container spacing={24}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <div className={classes.pageNavigatorContainer}>
                      <div>
                        <Typography variant="h5" className={classes.pageTitle}>{LocalizedString.field.title}</Typography>
                      </div>
                      <div className={classes.grow} />
                      <div>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="large"
                          classes={{ containedSecondary: classes.btnNewBooking }}
                          onClick={onAddNewPressed}
                        >
                          {LocalizedString.field.btnAddNew}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardHeader
                    title={(
                      <div className={classes.tableHeader}>
                        <Typography variant="h6" className={classes.detailHeader}>
                          {LocalizedString.field.title}
                        </Typography>
                        <div className={classes.row}>
                          {this.renderTableItemPerRow()}
                          <div style={{ width: 20 }} />
                          <Button variant="contained" className={classes.approve} onClick={() => onFilterPressed()}>
                            {LocalizedString.response.labelFilter}
                          </Button>
                        </div>
                      </div>
                )}
                  />
                  <CardContent>
                    <div style={{ width: '100%', overflow: 'auto' }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {this.renderTableCellHeader(LocalizedString.field.labelForm,
                              TABLE_FIELD_FIELD_ORDER)}
                            {this.renderTableCellHeader(LocalizedString.field.labelName,
                              TABLE_FIELD_FIELD_TITLE)}
                            {this.renderTableCellHeader(LocalizedString.field.labelOrder,
                              TABLE_FIELD_FIELD_STATUS)}
                            {this.renderTableCellHeader(LocalizedString.field.labelType,
                              TABLE_FIELD_FIELD_STATUS)}
                            <TableCell>{LocalizedString.common.labelActions}</TableCell>
                          </TableRow>
                          <TableRow>
                            {this.renderTableCellStringFilter('', true)}
                            {this.renderTableCellStringFilter(TABLE_FIELD_FIELD_NAME)}
                            {this.renderTableCellStringFilter('', true)}
                            {this.renderTableCellStringFilter('', true)}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {fields && fields.map(field => (
                            <TableRow>
                              {this.renderTableCellBody(field.form.title || '')}
                              {this.renderTableCellBody(field.name || '')}
                              {this.renderTableCellBody(field.order, RXFIELD_FIELD_ORDER)}
                              {this.renderTableCellBody(field.type || '')}
                              {this.renderTableCellAction(field)}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {this.renderTablePagination()}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </div>
        </div>

        <FieldAddDialog
          open={dialogAddStatus}
          onClose={onAddDialogClosePressed}
        />
        <FieldEditDialog
          open={dialogEditStatus}
          onClose={onEditDialogClosePressed}
        />
        <FieldDetailDialog
          open={dialogDetailStatus}
          onClose={onDetailDialogClosePressed}
        />
        <FieldDeleteDialog
          open={dialogDeleteStatus}
          onClose={onDeleteDialogClosePressed}
        />
        <FieldFilterDialog
          open={dialogFilterStatus}
          onClose={onFilterDialogClosePressed}
        />
        {downloading && <LoadingIndicator />}
      </>
    );
  }
}
FieldBase.propTypes = {
  classes: PropTypes.shape(PropTypes.any).isRequired,
  menuOpen: PropTypes.bool.isRequired,
  downloading: PropTypes.bool.isRequired,

  fields: PropTypes.arrayOf(PropTypes.any).isRequired,

  dialogAddStatus: PropTypes.bool.isRequired,
  dialogEditStatus: PropTypes.bool.isRequired,
  dialogDetailStatus: PropTypes.bool.isRequired,
  dialogDeleteStatus: PropTypes.bool.isRequired,
  dialogFilterStatus: PropTypes.bool.isRequired,

  filters: PropTypes.shape(PropTypes.any).isRequired,
  sortField: PropTypes.string.isRequired,
  sortDirection: PropTypes.string.isRequired,
  activePage: PropTypes.number.isRequired,
  totalPage: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,

  onAddNewPressed: PropTypes.func.isRequired,
  onAddDialogClosePressed: PropTypes.func.isRequired,
  onAppear: PropTypes.func.isRequired,
  onChangeRow: PropTypes.func.isRequired,
  onChangeSort: PropTypes.func.isRequired,
  onChangePaging: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  onDetailPressed: PropTypes.func.isRequired,
  onDetailDialogClosePressed: PropTypes.func.isRequired,
  onDeletePressed: PropTypes.func.isRequired,
  onDeleteDialogClosePressed: PropTypes.func.isRequired,
  onEditPressed: PropTypes.func.isRequired,
  onEditDialogClosePressed: PropTypes.func.isRequired,
  onFilterPressed: PropTypes.func.isRequired,
  onFilterDialogClosePressed: PropTypes.func.isRequired,
};

export default withStyles(styles)(FieldBase);
